import {
  Dataset, DocCache, HistorySync, LeaseClient, OutboxQueue, Replica, ReplicaSync, ReviewStore,
  Session, TokenFetchError, Transport, claimTabIdentity, onStoreEvent, openWithin, sessionHoldTokens, verifyAndRepair,
  type ChangesBody, type FieldSnapshot, type SnapshotBody, type TransportState,
} from '@colplat/core-client';
import type { FieldVersion } from '@colplat/protocol';
import webledgerManifest from '@colplat/app-webledger';
import { escapeHtml } from '@colplat/ui-web';
import { apiFetch } from './api.js';
import { knownOffline, readCached } from './offlineReads.js';
import { forceUpdateNow, noteHealthyLoad } from './serviceWorker.js';
import { appHref, globals, wsUrl } from './globals.js';
import { getStoredSession, onSignedOutElsewhere, returnToLogin } from './auth-storage.js';
import { presenceUserFor } from './identity.js';
import {
  foldIntoQueuedCreate, pendingDeletesByEntity, pendingRestoresByEntity, pendingRowsFromOutbox,
  type QueuedTransactionPayload,
} from './views/webledgerOfflineState.js';
import { flushQueuedDocuments, flushSays, singleFlight, type FlushResult } from './webledgerOutboxFlush.js';
import { loadPrefs, type PrefsHandle } from './views/webledgerPrefs.js';
import { mountChipTally } from './views/webledgerChipTally.js';
import { setCurrencyOrder } from './views/webledgerCurrencyOrder.js';
import { datasetLedger } from './views/webledgerLedger.js';

/**
 * Shared bootstrap + page chrome for webledger's three pages (accounts,
 * transactions, settings) — split out of what used to be one crowded page
 * (see the "less crowded UX" discussion) so each page can be reasonably
 * uncrowded on its own, especially on mobile. Navigation between them is a
 * plain `<a href>` full page load, same as every other route in this bundle
 * (main.ts) — not a client-side router, so each page independently runs this
 * bootstrap and opens its own WebSocket connection.
 */

export interface WebledgerClient {
  token: string;
  userId: string;
  /** Webledger roles, for deciding what to offer — never what to permit. */
  roles: string[];
  /** Whether to offer the instance-wide tools. Same caveat as `roles`. */
  superAdmin: boolean;
  session: Session;
  leaseClient: LeaseClient;
  transport: Transport;
  /** Last-known-good REST responses, so a dropped connection is not a dead page — see offlineReads.ts. */
  docCache: DocCache;
  /** Every replicated row of the ledger, held locally so filters and totals compute here (ADR-0025). */
  dataset: Dataset;
  /** What is keeping `dataset` level with the server. */
  replicaSync: ReplicaSync;
  /** What fills in each transaction's change history behind the scenes. */
  historySync: HistorySync;
  outbox: OutboxQueue;
  /** This person's server-synced preferences, loaded once per page (webledgerPrefs.ts). */
  prefs: PrefsHandle;
  /**
   * Re-read the outbox and lay what it holds over the local copy: records to
   * hide as deleted, to show as restored, and — since 2026-10-21 — entries
   * created or changed here and not sent yet (`Dataset.setPendingRows`).
   * Call after anything that queues, cancels or sends.
   */
  refreshPendingWork: () => Promise<void>;
  /**
   * Save a transaction on this device, to be sent when it can be — offline,
   * or when the connection went between opening the entry and pressing Save
   * (ADR-0011). Shows at once everywhere, and is sent straight away if the
   * server can be reached after all. An edit of an entry that has not been
   * sent yet stays its create (`foldIntoQueuedCreate`).
   */
  queueTransaction: (op: 'create' | 'update', payload: QueuedTransactionPayload) => Promise<void>;
  /**
   * Send what is queued, from whichever page is open. Joins one in flight.
   * Runs by itself on reconnect, on coming back to the tab and after
   * queueing; exposed for a page that wants to wait for it.
   */
  flushOutbox: () => Promise<FlushResult | undefined>;
  /** Told after every flush that sent, held or refused anything. */
  onOutboxFlushed: (fn: (result: FlushResult) => void) => () => void;
  /**
   * Entries the server would not accept as they are, oldest first — shown on
   * their rows and in a note at the top of the page until changed or thrown
   * away (ADR-0011, 2026-10-21).
   */
  refusedEntries: () => readonly RefusedEntry[];
  /** Told whenever what is queued has changed: queued, sent, refused, discarded. */
  onPendingWorkChange: (fn: () => void) => () => void;
  /** Throw away one queued entry — the unsent version — and go back to what the server has. */
  discardQueued: (outboxId: string) => Promise<void>;
  /**
   * Drop a refused entry for a record that has just been saved some other way
   * — online, from the server's copy — so the note does not outlive it.
   */
  clearRefused: (recordId: string) => Promise<void>;
  notify: (message: string) => void;
}

/** One refused entry, as the note and the row need it. */
export interface RefusedEntry {
  outboxId: string;
  recordId: string;
  op: string;
  description: string;
  txDate: string;
  message: string;
  at: string;
}

/** How long startup waits for a local store before carrying on without it. */
const STORE_WAIT_MS = 4000;

/**
 * Say, on screen and in the server's log, when a local store holds this page
 * up — the case that used to draw headers and no data until every window was
 * closed. Once per store and kind, so a page does not report itself to death.
 */
function watchLocalStores(token: string): void {
  const said = new Set<string>();
  onStoreEvent((event) => {
    const key = `${event.kind}:${event.store}`;
    if (said.has(key)) return;
    said.add(key);
    console.warn('[webledger] local store:', event);
    // To the server, so the next time this happens its log names the store
    // and the kind — what two incidents without it could not.
    void apiFetch('/apps/webledger/client-report', {
      method: 'POST', token, keepalive: true,
      body: { kind: event.kind, store: event.store, page: location.pathname, ua: navigator.userAgent },
    }).catch(() => undefined);
    if (event.kind === 'failed' && event.outdated) {
      // This page runs older code than what is stored — the old script from
      // a service worker still waiting to take over. The current version is
      // the cure, and nothing on this page can be holding work yet.
      showStoreBar('This window is running an older version of the app than the one that saved '
        + 'this device\u2019s copy. Updating\u2026', false);
      forceUpdateNow();
    } else if (event.kind === 'blocking') {
      showStoreBar('Another window of this app needed this device\u2019s saved copy, so this window let go '
        + 'of it. Reload this window to carry on.', true);
    } else if (event.kind === 'blocked' || event.kind === 'slow' || event.kind === 'failed') {
      showStoreBar('Another window of this app is holding this device\u2019s saved copy. This window is '
        + 'working from the server instead; reload the other windows (or close them) and this one '
        + 'will use it again.', false);
    }
  });
}

/** One quiet bar at the foot of the page: text, and a Reload button where reloading is the fix. */
function showStoreBar(message: string, reload: boolean): void {
  const draw = (): void => {
    let bar = document.getElementById('cfStoreBar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'cfStoreBar';
      bar.className = 'cf-updatebar';
      bar.setAttribute('role', 'status');
      bar.setAttribute('aria-live', 'polite');
      document.body.append(bar);
    }
    bar.innerHTML = `<span>${escapeHtml(message)}</span>`
      + (reload ? '<button type="button" class="btn btn-sm btn-primary" id="cfStoreReload">Reload</button>' : '')
      + '<button type="button" class="btn btn-sm btn-link" id="cfStoreLater">Dismiss</button>';
    bar.querySelector('#cfStoreReload')?.addEventListener('click', () => { window.location.reload(); });
    bar.querySelector('#cfStoreLater')?.addEventListener('click', () => { bar?.remove(); });
  };
  if (document.body) draw(); else document.addEventListener('DOMContentLoaded', draw, { once: true });
}

export async function bootstrapWebledgerClient(root: HTMLElement): Promise<WebledgerClient | undefined> {
  const stored = getStoredSession();
  if (!stored) {
    window.location.assign(appHref('/login'));
    return undefined;
  }
  onSignedOutElsewhere(() => window.location.assign(appHref('/login')));
  const token = stored.token;
  const userId = stored.userId;

  /*
   * Watch the local stores before opening any (2026-10-21). Reported twice
   * from prod: after a reload, every page showed headers and no data until
   * all windows were closed — startup was waiting on a store another window
   * held. Each store now gives up after a few seconds and the page carries
   * on; this says so on screen and in the server's log.
   */
  watchLocalStores(token);
  const docCache = new DocCache({ appId: 'webledger', env: globals().env });
  await docCache.open({ waitAtMostMs: STORE_WAIT_MS });

  /**
   * Read through the cache, because this one `await` used to take the whole
   * app down offline: `apiFetch` rejects when there is no network, the
   * rejection propagated out of this function, and every page that calls it
   * rendered nothing at all. Navigating between webledger's pages while
   * offline produced a blank screen — the offline handling further down never
   * got the chance to run.
   *
   * A 401 still redirects: that is the server answering, and `readCached`
   * deliberately does not fall back on an answer (see offlineReads.ts). If we
   * are merely unreachable we carry on with the last known display name, or
   * none — the session token in `localStorage` is what authorises anything we
   * do, and a write made on a dead session is rejected server-side regardless
   * of what this call would have said.
   */
  const meRead = await readCached<{ displayName: string; roleGrants?: Array<{ appId: string; role: string }> }>(
    docCache, `auth:me:${userId}`, '/auth/me', token,
  );
  if (!meRead.ok && meRead.status === 401) {
    // `returnToLogin`, not a bare redirect: the token has to go first or
    // `/login` resumes on it and sends us straight back here.
    returnToLogin();
    return undefined;
  }
  const me = meRead.ok ? meRead.data : { displayName: '', roleGrants: [] };
  /**
   * This user's webledger roles, for deciding what to *offer*. Never what to
   * permit: every endpoint checks for itself (invariant #2's reasoning
   * applied to rights), so a stale or spoofed list here shows a button that
   * the server then refuses.
   */
  const roles = (me.roleGrants ?? []).filter((g) => g.appId === 'webledger').map((g) => g.role);

  /*
   * Whole-instance backup is a super-admin affair, and super-admin is a role
   * on `administration`, not on webledger (ADR-0018 keeps it an ordinary
   * additive role rather than a flag) — so it is read from the grants
   * directly rather than from `roles` above.
   *
   * The shell was rendered before this ran, which is true on every page, so
   * the entry is revealed here and the bar re-measured: an item that appears
   * after the overflow has been calculated would otherwise sit outside it.
   */
  const superAdmin = (me.roleGrants ?? [])
    .some((g) => g.appId === 'administration' && g.role === 'super-admin');
  if (superAdmin) {
    for (const el of root.querySelectorAll<HTMLElement>('[data-nav-admin]')) el.hidden = false;
    wireNavOverflow(root);
  }

  // Fresh per tab, in memory only (SPEC §3.3) — same reasoning as administration.ts.
  const node = crypto.randomUUID();

  const transport = new Transport({
    wsUrl,
    fetchToken: async () => {
      const res = await apiFetch('/auth/ws-token', { method: 'POST', token, body: { appId: 'webledger' } });
      // `fatal` only when the server actually refused us: a 5xx or a rejected
      // `fetch` (offline) must stay retryable, or one drop is terminal.
      // See core-client's `TokenFetchError`.
      if (!res.ok) throw new TokenFetchError('could not mint ws token', res.status === 401 || res.status === 403);
      const data = (await res.json()) as { token: string };
      return { token: data.token, expiresInSeconds: 60 };
    },
    node,
    schemaVersion: webledgerManifest.schemaVersion,
  });

  /*
   * The row replica, and the thing that catches it up (ADR-0025).
   *
   * Opened before the socket connects so that a page mounting immediately
   * afterwards reads a hydrated store rather than an empty one — the rows
   * come back from IndexedDB synchronously once `open()` has resolved, and a
   * screen that painted before that would paint an empty ledger and then
   * flicker.
   *
   * The first sync is deliberately NOT awaited. A cold device needs a
   * snapshot and a snapshot needs the network; blocking the page on it would
   * reintroduce exactly the failure this replaces, where one unreachable
   * request left the whole app blank.
   */
  const dataset = new Dataset({
    appId: 'webledger',
    env: globals().env,
    /*
     * Whose history a changed row belongs to.
     *
     * A transaction's history is not only its own field writes: its splits,
     * its tag links, its bank reference and its attachments are separate
     * records whose changes are part of the same story (ADR-0021's document
     * group, and the reason the history endpoint gathers them). So any row
     * carrying a `transactionId` invalidates that transaction's copy, and a
     * transaction row invalidates its own.
     */
    historyOwnerOf: (entity, row) => {
      if (entity === 'transaction') return row.id;
      const owner = row['transactionId'];
      return typeof owner === 'string' ? owner : undefined;
    },
  });
  await dataset.open({ fallbackAfterMs: STORE_WAIT_MS });
  if (dataset.isTemporary()) window.addEventListener('pagehide', () => { dataset.discardTemporary(); });
  /*
   * Currencies are listed in the order the Settings page gives them, on
   * every page — set here, first, so it is in place before any page draws,
   * and kept current ahead of the page's own repaint because this
   * subscription is the first one made.
   */
  const syncCurrencyOrder = (): void => { setCurrencyOrder(datasetLedger(dataset).currencies()); };
  syncCurrencyOrder();
  dataset.subscribe(syncCurrencyOrder);
  /** `?a=b` or nothing — an empty `?` is not a URL anyone wants in a log. */
  const query = (q: URLSearchParams): string => (q.size > 0 ? `?${q.toString()}` : '');

/**
 * A page cursor as the server will parse it back: `entity:<ms>.<uuid>`.
 *
 * Reassembled here because the server hands the parts back as an object, and
 * getting the spelling wrong does not fail loudly — the server simply cannot
 * read it, drops `after`, and re-serves the same first page until the client
 * gives up with "delta did not terminate". The shape lives in exactly one
 * place for that reason; see `parsePageCursor` in services/api/src/replica.ts.
 */
function pageCursorParam(at: { entity: string; ms: number; id: string }): string {
  return `${at.entity}:${at.ms}.${at.id}`;
}


  const replicaSync = new ReplicaSync({
    dataset,
    fetchSnapshot: async (from) => {
      const q = new URLSearchParams();
      if (from) {
        q.set('after', pageCursorParam(from.after));
        q.set('cursor', from.cursor);
      }
      const res = await apiFetch(`/apps/webledger/replica/snapshot${query(q)}`, { token });
      if (!res.ok) throw new Error(`snapshot: ${res.status}`);
      return (await res.json()) as SnapshotBody;
    },
    fetchChanges: async (since, within) => {
      const q = new URLSearchParams({ since });
      if (within) {
        q.set('until', within.until);
        q.set('after', pageCursorParam(within.after));
      }
      const res = await apiFetch(`/apps/webledger/replica/changes${query(q)}`, { token });
      if (!res.ok) throw new Error(`changes: ${res.status}`);
      return (await res.json()) as ChangesBody;
    },
  });

  const historySync = new HistorySync({
    dataset,
    candidates: () => dataset.rows('transaction').map((t) => t.id),
    fetchHistories: async (ids) => {
      // POST, because 200 uuids is 7.4 KB of query string and more than one
      // proxy between here and the box declines to forward that.
      const res = await apiFetch('/apps/webledger/history', { token, method: 'POST', body: { ids } });
      if (!res.ok) throw new Error(`histories: ${res.status}`);
      return ((await res.json()) as { histories: Record<string, unknown> }).histories;
    },
  });

  /*
   * Holds outlive the connection and the tab's own reloads (ADR-0003,
   * 2026-10-22): this tab keeps its tokens in `sessionStorage` to resume
   * them, and releases them on its way out of the page by a request that
   * outlives it. Who this tab is has to be settled first — a duplicated tab
   * inherits the storage, and must not resume its original's holds.
   */
  await claimTabIdentity();
  // A page brought back from the back/forward cache left through `pagehide`,
  // which released its holds, and its socket is gone: load it afresh rather
  // than show a form that believes it still holds anything.
  window.addEventListener('pageshow', (event) => { if (event.persisted) window.location.reload(); });
  const leaseClient = new LeaseClient({
    transport,
    tokens: sessionHoldTokens(typeof sessionStorage === 'undefined' ? undefined : sessionStorage),
    releaseOnExit: (key, holdToken) => {
      void apiFetch('/apps/webledger/holds/release', {
        method: 'POST', token, keepalive: true, body: { holds: [{ key, token: holdToken }] },
      }).catch(() => undefined);
    },
  });
  const outbox = new OutboxQueue({ appId: 'webledger', env: globals().env });
  // Never temporary — it holds unsent work — so the page does not wait for it
  // past a few seconds; whatever needs it waits instead.
  await outbox.open({ waitAtMostMs: STORE_WAIT_MS });
  const replica = new Replica({
    appId: 'webledger',
    env: globals().env,
    schemaVersion: webledgerManifest.schemaVersion,
    fetchRecord: async (entity, recordId) => {
      // `Session.openRecord` calls this, and webledger opens a record on
      // every modal. With no network the request is certain to fail and
      // costs a connection timeout first, so do not make it — an empty
      // result is exactly what a failure would have produced anyway, and
      // this app reads records over the document endpoints regardless.
      if (knownOffline()) return { fields: [] };
      /*
       * A transport failure is an empty result, not a throw.
       *
       * `knownOffline()` only catches `navigator.onLine === false`; a server
       * that is down, a captive portal or a dropped VPN all report online and
       * make `apiFetch` reject. That rejection used to propagate out of
       * `Session.openRecord` and kill whatever called it — which meant a row
       * would not open at all under exactly the conditions the local replica
       * now makes openable. An empty result is what a failure was always
       * going to produce anyway; this app reads records over the document
       * endpoints and the replica regardless.
       */
      try {
        const res = await apiFetch(`/apps/webledger/records/${entity}/${recordId}`, { token });
        if (!res.ok) return { fields: [] };
        return (await res.json()) as { fields: FieldSnapshot[]; history?: FieldVersion[] };
      } catch {
        return { fields: [] };
      }
    },
  });
  await replica.open({ fallbackAfterMs: STORE_WAIT_MS });
  window.addEventListener('pagehide', () => { replica.discardTemporary(); });

  // Transactions ARE edited offline (ADR-0011), and a held one is reviewed
  // over REST by the review banner (views/webledgerReviewBanner.ts) on the
  // wallets and transactions pages. This store is the field-mode path, which
  // no webledger entity takes yet — hence the stub.
  const review = new ReviewStore({
    fetchPendingRevisions: async () => [],
    postPendingRevisionResolution: async () => {},
  });

  const session = new Session({
    // Offline with no cached identity yet: presence naming is cosmetic, and
    // an honest placeholder beats failing to start.
    me: presenceUserFor(userId, me.displayName || 'You'),
    manifest: webledgerManifest,
    transport, leaseClient, outbox, replica, review, node,
    // account/currency are the only field-mode entities in this pass, and
    // neither is document-mode — this callback is exercised only if that
    // ever changes.
    commitDocumentRest: async () => ({ ok: false, reason: 'validation' }),
  });

  const liveRegion = announcementRegion(root);
  const notify = (message: string): void => { if (liveRegion) liveRegion.textContent = message; };
  session.onNotice(notify);

  wireConnectionIndicator(root, transport);

  /**
   * `sync` has refused this client for being too old to write safely
   * (ADR-0016). Nothing subscribed to this before, so the app simply sat
   * there with a dead socket. It matters more now: a cache-first app shell
   * (ADR-0022) could otherwise pin someone to a bundle the server will not
   * talk to — permanently broken rather than merely stale. This is the one
   * case where reloading unasked is the kinder outcome, since no write can
   * succeed either way.
   */
  transport.onSchemaStale(() => {
    notify('This app has been updated and needs to reload.');
    forceUpdateNow();
  });

  /*
   * Anything the server says changed makes the replica stale.
   *
   * `recordEvent` already fans out every create, delete, restore and
   * document-mode update, so this needs no new wire message — and `poke` is
   * debounced, so a transaction saving eight splits costs one delta rather
   * than nine.
   *
   * A record this client has not subscribed to does not announce itself, so
   * the reconnect below is what catches whatever happened while away rather
   * than this.
   */
  transport.onMessage((msg) => {
    // `committed` is a field write, `recordEvent` a create/delete/restore or a
    // document-mode update. Between them they cover every durable change the
    // server announces, which is exactly the set that makes the replica stale.
    if (msg.t === 'recordEvent' || msg.t === 'committed') replicaSync.poke();
  });
  /*
   * Coming back to the tab — a laptop opened, a window brought forward —
   * asks for whatever changed meanwhile, straight away. The socket may still
   * look open while being dead after a sleep, and would announce nothing
   * until something noticed; a delta costs one small request and joins any
   * already in flight.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') replicaSync.poke();
  });
  transport.onStateChange((state) => {
    // Coming back is the only moment the socket cannot tell us what we missed:
    // fan-out is per-subscription and a record nobody subscribed to announced
    // nothing. The delta covers it.
    if (state === 'open') {
      // The socket is up, so whatever reloading it took to get here worked.
      // Forgetting that tally is what keeps the NEXT ordinary update silent
      // instead of asking permission the user has already given by reloading.
      noteHealthyLoad();
      replicaSync.poke();
      void verifyReplica();
    }
  });

  /*
   * Prove the local copy matches, on every connect (ADR-0025).
   *
   * The delta is only correct if no change was ever missed, which is a
   * property of every write path agreeing to announce itself — an assumption
   * that had already been broken four times over before anybody checked. This
   * is the part that does not depend on remembering: fold what is held,
   * compare with the server, refetch whatever disagrees.
   *
   * After the delta rather than instead of it, so the ordinary case stays one
   * small request and this only ever confirms it. Failures are silent: an
   * unverified replica is the state we were already in, and a page that
   * refused to work because it could not check would be worse than one
   * working on a copy that is almost certainly right.
   */
  async function verifyReplica(): Promise<void> {
    try {
      const repaired = await verifyAndRepair({
        dataset,
        fetchDigest: async () => {
          const res = await apiFetch('/apps/webledger/replica/digest', { token });
          if (!res.ok) throw new Error(`digest: ${res.status}`);
          return (await res.json()) as never;
        },
        fetchEntity: async (entity, after) => {
          const q = new URLSearchParams();
          if (after) q.set('after', after);
          const res = await apiFetch(`/apps/webledger/replica/entity/${entity}${query(q)}`, { token });
          if (!res.ok) throw new Error(`entity: ${res.status}`);
          return (await res.json()) as never;
        },
      });
      if (repaired.length > 0) {
        console.info('[replica] repaired after digest mismatch:', repaired.join(', '));
      }
    } catch {
      // See above.
    }
  }

  /*
   * History follows the rows, never leads them.
   *
   * It is fetched after a successful sync and only for what is missing, in a
   * few chunks at a time — a background fill, not something any page waits
   * for. A failure here is silent on purpose: the panel falls back to asking
   * the server directly, which is exactly what it did before any of this.
   */
  dataset.subscribe(() => { void historySync.fill().catch(() => undefined); });

  transport.connect();
  // Not awaited: a cold device needs the network for its first snapshot, and
  // blocking the page on that is the failure this whole design replaces.
  void replicaSync.sync().then(() => verifyReplica());
  primeReferenceCaches(docCache, token);

  const prefs = await loadPrefs(docCache, token);
  // Mounted here rather than per page: the tally works on every currency chip
  // in the app and follows the reader between pages, so "which pages have it"
  // is not a question any page should get to answer differently.
  //
  // Wrapped, because this function is the gate every page passes through: a
  // throw here would leave the caller's `await` rejected and the page as an
  // empty shell, with a reload doing exactly the same thing again. A running
  // total in the corner is worth precisely nothing against that, so it fails
  // quietly on its own and the ledger opens regardless.
  try {
    mountChipTally({ root, prefs, docCache, token });
  } catch (err) {
    console.warn('[webledger] the chip tally could not start', err);
  }
  /*
   * Which records every list should be hiding because of an unsent delete.
   *
   * Recomputed from the outbox rather than tracked alongside it: the queued
   * delete is the fact. Called here at boot so a delete made offline is still
   * applied after a reload with no connection, and again by anything that
   * changes the queue — the editor when it queues or undoes one, the flush
   * when the server accepts or refuses it.
   */
  let refused: RefusedEntry[] = [];
  const pendingListeners = new Set<() => void>();
  const refreshPendingWork = async (): Promise<void> => {
    const queued = await outbox.allDocuments();
    refused = queued.filter((q) => q.entity === 'transaction' && q.refused).map((q) => {
      const p = (q.payload ?? {}) as { description?: string; txDate?: string };
      return {
        outboxId: q.id, recordId: q.recordId, op: q.op, description: p.description ?? '',
        txDate: p.txDate ?? '', message: q.refused!.message, at: q.refused!.at,
      };
    });
    dataset.setPendingDeletes(pendingDeletesByEntity(queued));
    // The mirror image, and read from the same list in the same pass: a
    // record with an unsent restore is shown everywhere at once, not just on
    // the page it was restored from.
    dataset.setPendingRestores(pendingRestoresByEntity(queued));
    // And unsent creates and edits, laid over what the server last said.
    type Row = Record<string, unknown> & { id: string };
    dataset.setPendingRows(pendingRowsFromOutbox(queued, {
      transaction: (id) => dataset.anyRow<Row>('transaction', id),
      tagsOf: (txId) => dataset.serverRows<Row & { transactionId: string; tagId: string }>('transactionTag')
        .filter((t) => t.transactionId === txId),
      transfersOf: (txId) => dataset.serverRows<Row>('walletTransfer').filter((t) => t['transactionId'] === txId),
    }));
    for (const fn of pendingListeners) fn();
  };
  // Waited for only briefly: with the outbox held up by another window this
  // would wait as long as it does, and the page with it. It applies itself
  // whenever the outbox does open.
  await openWithin(refreshPendingWork().catch((err: unknown) => {
    // A dataset that cannot read the outbox would show deleted records as
    // present, which is wrong but not dangerous — and failing the whole
    // bootstrap over it would be.
    console.warn('[webledger] unsent deletes could not be applied to the local copy', err);
  }), STORE_WAIT_MS, 'outbox (unsent work)');

  const flushedListeners = new Set<(result: FlushResult) => void>();
  /*
   * Sending is a REST matter, not a socket one, so it does not wait for the
   * socket: a dead-looking socket after a sleep must not keep somebody's work
   * on the device. Only a browser that knows it has no network is skipped.
   */
  const flushOutbox = singleFlight(async (): Promise<FlushResult | undefined> => {
    if (knownOffline()) return undefined;
    if ((await outbox.sendableDocuments()).length === 0) return undefined;
    const result = await flushQueuedDocuments({
      token,
      sendable: () => outbox.sendableDocuments(),
      remove: (id) => outbox.removeDocument(id),
      refuse: (id, message) => outbox.markDocumentRefused(id, message),
    });
    if (result.synced + result.held + result.refusedDeletes + result.refused > 0) {
      // The server's copy first, then the overlay taken off — the other
      // order shows the entry as it was before it was sent, for a moment.
      await replicaSync.sync().catch(() => undefined);
      await refreshPendingWork();
      const said = flushSays(result);
      if (said) notify(said);
      for (const fn of flushedListeners) fn(result);
    }
    return result;
  });
  const queueTransaction = async (op: 'create' | 'update', payload: QueuedTransactionPayload): Promise<void> => {
    const queued = await outbox.allDocuments();
    const unsentCreate = op === 'update'
      ? queued.find((q) => q.entity === 'transaction' && q.recordId === payload.id && q.op === 'create' && !q.held)
      : undefined;
    await outbox.enqueueDocument({
      entity: 'transaction',
      recordId: payload.id,
      op: unsentCreate ? 'create' : op,
      payload: unsentCreate ? foldIntoQueuedCreate(unsentCreate.payload as QueuedTransactionPayload, payload) : payload,
      clock: session.nextClock(),
    });
    await refreshPendingWork();
    void flushOutbox();
  };
  // Whenever there is a chance of reaching the server again.
  transport.onStateChange((state) => { if (state === 'open') void flushOutbox(); });
  window.addEventListener('online', () => { void flushOutbox(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushOutbox();
  });
  void flushOutbox();

  return {
    token, userId, roles, superAdmin, session, leaseClient, transport, docCache,
    dataset, replicaSync, historySync, outbox, prefs, notify, refreshPendingWork,
    queueTransaction, flushOutbox,
    onOutboxFlushed: (fn) => { flushedListeners.add(fn); return () => flushedListeners.delete(fn); },
    refusedEntries: () => refused,
    onPendingWorkChange: (fn) => { pendingListeners.add(fn); return () => pendingListeners.delete(fn); },
    discardQueued: async (outboxId) => { await outbox.removeDocument(outboxId); await refreshPendingWork(); },
    clearRefused: async (recordId) => {
      const stale = refused.filter((r) => r.recordId === recordId);
      if (stale.length === 0) return;
      for (const r of stale) await outbox.removeDocument(r.outboxId);
      await refreshPendingWork();
    },
  };
}

/**
 * Warm the cache for webledger's small shared reference lists, in the
 * background, from whichever page the user happens to open.
 *
 * Each page only ever fetched what it needed, so the caches were per-page and
 * navigating offline to a page you had never visited online showed nothing —
 * reported as "switching to accounts offline displays no accounts", which was
 * accurate: nothing had ever cached them. Four small lists, fetched after the
 * page is already interactive, make the whole app offline-capable after a
 * single online visit to any of it.
 *
 * `transactions` is deliberately not primed here: it is the one large read,
 * and the page that needs it fetches it anyway.
 */
function primeReferenceCaches(docCache: DocCache, token: string): void {
  const lists: Array<[string, string]> = [
    ['accounts', '/apps/webledger/accounts'],
    ['currencies', '/apps/webledger/currencies'],
    ['purposes', '/apps/webledger/purposes'],
    ['account-purposes', '/apps/webledger/account-purposes'],
  ];
  void (async () => {
    for (const [key, path] of lists) {
      if (docCache.get(key)) continue; // already have a copy; the owning page refreshes it
      await readCached(docCache, key, path, token);
    }
  })();
}

export type WebledgerPage =
  | 'wallets' | 'log' | 'budgets' | 'debts'
  | 'accounts' | 'bank' | 'settings'
  | 'transactions' | 'reports' | 'backup' | 'deleted';

/**
 * The nav, in the order the work happens.
 *
 * The four wallet views lead because they are what this ledger is read
 * through day to day; the three places you go to set something up follow.
 * `overflow: true` puts an item behind the disclosure at every width.
 *
 * `badge` names a slot the page can fill — "4 over" on Wallets, a debt count
 * on Between wallets. Those counts used to live on the in-page pills that
 * this bar replaces, and they are the reason it is worth looking at: a
 * number that only appears once you are already on the page is a number you
 * find by accident.
 */
interface NavItem {
  page: WebledgerPage;
  href: string;
  label: string;
  /** Behind the disclosure whatever the width. */
  overflow?: boolean;
  /** Needs bank sync to be configured. */
  bankOnly?: boolean;
  /**
   * Only offered to a super-admin — and only *offered*: the routes behind it
   * check the role themselves (invariant #2's reasoning applied to rights),
   * so hiding it is tidiness, not security.
   *
   * It is rendered `hidden` and revealed after bootstrap, because every page
   * puts the shell up before it knows who is looking.
   */
  adminOnly?: boolean;
  badge?: boolean;
}

export const WEBLEDGER_NAV: readonly NavItem[] = [
  { page: 'wallets', href: '/webledger/wallets', label: 'Wallets', badge: true },
  { page: 'log', href: '/webledger/wallets/log', label: 'Log', badge: true },
  { page: 'budgets', href: '/webledger/wallets/budgets', label: 'Budgets', badge: true },
  { page: 'debts', href: '/webledger/wallets/between', label: 'Between wallets', badge: true },
  { page: 'accounts', href: '/webledger', label: 'Accounts' },
  { page: 'bank', href: '/webledger/bank', label: 'Bank sync', bankOnly: true },
  { page: 'settings', href: '/webledger/settings', label: 'Settings' },
  // Behind the disclosure by decision, not by width. The wallet log is
  // already a filterable list of every transaction, so the separate list is
  // the one that repeats itself.
  { page: 'transactions', href: '/webledger/transactions', label: 'Transactions', overflow: true },
  { page: 'reports', href: '/webledger/reports', label: 'Reports', overflow: true },
  // Instance-wide rather than webledger's own, but this is the nav the people
  // who would reach for it are already looking at.
  // Not admin-only: deleting needs only membership, and somebody who could
  // remove a record should not have to find an admin to put it back.
  { page: 'deleted', href: '/webledger/deleted', label: 'Deleted records', overflow: true },
  { page: 'backup', href: '/webledger/backup', label: 'Backup & restore', overflow: true, adminOnly: true },
];

/**
 * Every item is rendered once, in the bar, and the bar hides what will not
 * fit — it does not render a second copy into the menu.
 *
 * Two lists of the same links is how one of them goes stale, and it is also
 * how a link ends up reachable twice by keyboard while appearing once. So
 * the menu holds the always-overflowed pair, and `wireNavOverflow` moves the
 * rest between the two by measuring, one at a time from the right, which is
 * what "the main players move into the list one by one" asks for.
 */
export function webledgerShellHtml(active: WebledgerPage, bodyHtml: string): string {
  const shown = WEBLEDGER_NAV.filter((item) => !item.bankOnly || globals().bank);
  const link = (item: NavItem): string =>
    `<li class="nav-item" data-nav="${item.page}"${item.overflow ? ' data-nav-keep' : ''}${
      item.adminOnly ? ' data-nav-admin hidden' : ''}>
      <a class="nav-link ${item.page === active ? 'active' : ''}" href="${appHref(item.href)}">${escapeHtml(item.label)}${
      item.badge ? `<span class="badge ms-1" data-nav-badge="${item.page}" hidden></span>` : ''}</a>
    </li>`;

  /*
   * The lens the floating surfaces refract through.
   *
   * Noise, blurred, used to push the backdrop's pixels around — which is
   * what makes a panel look like glass rather than like frosting. It has to
   * live in the document because CSS references it by fragment, so it rides
   * with the shell every page puts up.
   *
   * Only the panels use it. A currency chip tried it first and the page came
   * back pixel-identical: displacement needs something behind it to bend,
   * and a chip sits on a flat card. A wallet log also carries six hundred of
   * them, each of which would want its own backdrop sample.
   */
  const lens = `
    <svg class="cf-lens" aria-hidden="true" focusable="false">
      <filter id="cfGlassLens" x="0%" y="0%" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency="0.006 0.006" numOctaves="2" seed="42" result="noise"/>
        <feGaussianBlur in="noise" stdDeviation="2" result="soft"/>
        <!-- Small. A displacement map samples OUTSIDE the element it runs on,
             and what is outside a backdrop is nothing — so a large scale does
             not make a panel glassier, it makes it thinner, by mixing in
             transparent pixels at every edge. At 46 the entry form went
             see-through enough to read the ledger through the fields. -->
        <feDisplacementMap in="SourceGraphic" in2="soft" scale="14"
          xChannelSelector="R" yChannelSelector="G"/>
      </filter>
    </svg>`;

  return `${lens}
    <div class="container cf-shell py-3">
      <div class="wl-head-fold"><div class="wl-head-fold__in"><div class="wl-head mb-3">
        <!-- The chevron for the affordance, the word for what it does. A bare
             "<" sits a few pixels under the browser's own back button and
             means something else entirely — leave the app, not go back. -->
        <button class="btn btn-sm btn-outline-secondary wl-head__back" type="button" id="backBtn">
          <span aria-hidden="true">&lsaquo;</span> Apps</button>
        <span class="wl-head__brand">WebLedger</span>
        <ul class="nav nav-tabs wl-head__tabs" id="wlNav">
          ${shown.filter((i) => !i.overflow).map(link).join('')}
        </ul>
        <div class="wl-head__more">
          <button class="btn btn-sm btn-outline-secondary" type="button" id="wlMoreBtn"
            aria-expanded="false" aria-controls="wlMoreMenu" aria-label="More pages">More <span class="cf-chev" aria-hidden="true"></span></button>
          <ul class="wl-head__menu" id="wlMoreMenu" hidden>
            ${shown.filter((i) => i.overflow).map(link).join('')}
          </ul>
        </div>
        <span class="cf-conn" id="wlConn" role="status"></span>
      </div></div></div>
      ${pageNoticeHtml()}
      <div id="wlBody">${bodyHtml}</div>
    </div>`;
}

/** Put a count on a nav item, or take it away. Used by the wallets sections. */
export function setNavBadge(page: WebledgerPage, text: string, title?: string): void {
  const el = document.querySelector<HTMLElement>(`[data-nav-badge="${page}"]`);
  if (!el) return;
  el.hidden = text === '';
  el.textContent = text;
  if (title) el.title = title; else el.removeAttribute('title');
}

/**
 * The reflow bound to each nav bar, so a second `wireNavOverflow` on the
 * same page re-measures instead of wiring a second set of handlers.
 */
const navReflows = new WeakMap<HTMLElement, () => void>();

/**
 * The disclosure, and the overflow that feeds it.
 *
 * Measured rather than guessed at a breakpoint: the labels are words, and
 * "Between wallets" in one language is not the same width as in another.
 * Items leave the bar from the RIGHT, so the four wallet views are the last
 * to go — which is what makes them the main players rather than merely the
 * first ones listed.
 */
export function wireNavOverflow(root: ParentNode = document): void {
  const bar = root.querySelector<HTMLElement>('#wlNav');
  const menu = root.querySelector<HTMLElement>('#wlMoreMenu');
  const btn = root.querySelector<HTMLButtonElement>('#wlMoreBtn');
  if (!bar || !menu || !btn) return;

  /*
   * Called more than once on a page: `wireBackButton` wires the bar up
   * front, and bootstrap calls it again after revealing an admin-only item,
   * because an entry that appears later has to be measured in.
   *
   * A second pass re-runs the FIRST pass's reflow rather than building a
   * new one, for two reasons. Binding the click handler twice gives the
   * button two toggles, and the second undoes the first — the menu opens
   * and shuts inside one click, and the button looks dead. And `parked`
   * below is closure state: a fresh closure starts with an empty list and
   * no knowledge of what the first pass already moved into the menu, so
   * those items would never be returned to the bar when the window widened.
   */
  const already = navReflows.get(btn);
  if (already) { already(); return; }

  const close = (): void => { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
  /*
   * Hung from whichever edge keeps it on screen.
   *
   * It hangs from the button's RIGHT edge by default, which is right while
   * the button is near the right of the bar. Once the head wraps and the
   * button moves to the left, the same rule sends the menu off the left of
   * the window — open, painted, and entirely out of view, which reads as a
   * button that does nothing.
   */
  const place = (): void => {
    /*
     * The menu is `position:fixed` so that the log header's fold wrapper —
     * `overflow:hidden`, which is what lets it fold — cannot clip it. Fixed
     * means viewport coordinates, so the placement that used to be two CSS
     * rules is computed here.
     *
     * It hangs under the button and is pulled back from whichever edge it
     * would cross, rather than flipping between two anchors: once the head
     * wraps, the button can sit anywhere along the row, and "right-aligned
     * unless that goes off-screen" leaves it off-screen on the other side
     * just as easily.
     */
    const btnBox = btn.getBoundingClientRect();
    menu.style.top = `${btnBox.bottom + 4}px`;
    // Measured after the top is set but before the left is, so the width is
    // the real one; `min-width` and the longest label both feed it.
    const width = menu.getBoundingClientRect().width;
    const want = btnBox.right - width;
    const max = window.innerWidth - width - 8;
    menu.style.left = `${Math.round(Math.min(Math.max(8, want), Math.max(8, max)))}px`;
  };
  {
    btn.addEventListener('click', (event) => {
      event.stopPropagation();
      const open = menu.hidden;
      menu.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      if (open) place();
    });
    document.addEventListener('click', (event) => {
      if (!menu.hidden && !(event.target as HTMLElement).closest('.wl-head__more')) close();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
    /*
     * A fixed menu does not travel with the page, so a scroll would leave it
     * hanging over the content with its button somewhere else. Closing is
     * better than tracking: this is a navigation menu, and anybody scrolling
     * the page under it has moved on from it.
     *
     * Capture, because the scroll that matters is usually the log's own list
     * scroller rather than the document, and those do not bubble.
     */
    window.addEventListener('scroll', () => { if (!menu.hidden) close(); }, { capture: true, passive: true });
  }

  // The always-overflowed pair stays at the bottom of the menu; measured
  // items are inserted above it, in bar order.
  const parked: HTMLElement[] = [];
  const reflow = (): void => {
    // Everything back in the bar first, so a window getting WIDER returns
    // items rather than only ever taking them away.
    for (const item of parked.splice(0)) bar.appendChild(item);
    let guard = 0;
    while (bar.scrollWidth > bar.clientWidth + 1 && bar.children.length > 1) {
      if (guard++ > 50) break;
      const last = bar.lastElementChild as HTMLElement;
      parked.unshift(last);
      menu.insertBefore(last, menu.firstChild);
    }
  };
  navReflows.set(btn, reflow);
  reflow();
  // `ResizeObserver` rather than `resize`: the bar also changes width when a
  // scrollbar appears or the pane is dragged, neither of which fires one.
  new ResizeObserver(reflow).observe(bar.parentElement ?? bar);
}

/**
 * The page's announcement region — by name, not by being first.
 *
 * This used to be `root.querySelector('[aria-live]')`, which worked only
 * while a page had exactly one. The wallets page has four — a warning area
 * inside each of its dialogs — and the first of those sits inside a dialog
 * that is usually closed, so every notification on that page was written into
 * a box nobody could see and no screen reader would reach: "Saved.", "Could
 * not load the wallets", and the one that matters most, "the hold on this
 * transaction has lapsed". Nothing failed; the sentences simply went
 * somewhere else.
 *
 * `.cf-sr` is what the shell renders for exactly this, and every page has
 * one. The fallback keeps a page without one working, while still refusing
 * anything sealed inside a dialog.
 */
export function announcementRegion(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>('.cf-sr[aria-live]')
    ?? [...root.querySelectorAll<HTMLElement>('[aria-live]')].find((el) => !el.closest('dialog'))
    ?? null;
}

/**
 * The one visible home for a failure on any webledger page.
 *
 * `client.notify` writes into the `.cf-sr` region, which is
 * `position:absolute;width:1px;height:1px;clip:rect(0 0 0 0)` — an
 * announcement and nothing else. A sighted user saw *nothing at all* when an
 * operation failed, which is how "pressing it does nothing" gets reported for
 * a request that in fact came back with a real error. Failures now write to
 * both: `notify` for the announcement, this for the eyes.
 *
 * Deliberately not a live region itself, and not `role="status"` either.
 * `bootstrapWebledgerClient` binds `notify` to `.cf-sr`, and a live region
 * here would read the same sentence out twice, once from each element. This
 * element is for sighted users only — the announcement is already handled.
 *
 * Rendered once per page and merely unhidden, never inserted, and it sits
 * above `#wlBody` rather than inside anyone's form (invariant #11).
 */
function pageNoticeHtml(): string {
  return `<div class="alert alert-danger py-2 small mb-3" id="wlNotice" hidden></div>`;
}

/**
 * Show `message` on screen as well as announcing it. The glyph and the
 * wording carry the meaning; the red is decoration, never the only signal
 * (invariant #11).
 *
 * Scrolled into view because the act that failed is often far down a long
 * table — a drag in the account tree, a reorder at the bottom of a register —
 * and a banner at the top of the page the user cannot see is the same bug
 * again in a different costume.
 */
export function showPageNotice(root: HTMLElement, message: string): void {
  const el = root.querySelector<HTMLElement>('#wlNotice');
  if (!el) return;
  showNoticeAt(el, message);
  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

/**
 * The same failure, in a box that is not the page's.
 *
 * A modal covers the page, so `#wlNotice` behind it is the invisible bug
 * again in a different costume — a dialog that refuses has to say so inside
 * itself. Every such box is written and cleared through here so the glyph,
 * the escaping and the wording stay the same wherever a refusal lands.
 */
export function showNoticeAt(el: HTMLElement | null, message: string): void {
  if (!el) return;
  el.innerHTML = `<span aria-hidden="true">\u26a0</span> ${escapeHtml(message)}`;
  el.hidden = false;
}

/** Clear one, so a refusal never outlives the thing that caused it. */
export function clearNoticeAt(el: HTMLElement | null): void {
  if (!el) return;
  el.hidden = true;
  el.textContent = '';
}

/** Clear it — called when the failed act is retried, so a stale error never outlives its cause. */
export function clearPageNotice(root: HTMLElement): void {
  clearNoticeAt(root.querySelector<HTMLElement>('#wlNotice'));
}

/**
 * The pairing every failure path wants: announce it *and* show it. Built once
 * per page from `client.notify` and handed to the helpers that can fail.
 */
export function pageFailure(root: HTMLElement, notify: (message: string) => void): (message: string) => void {
  return (message: string): void => {
    notify(message);
    showPageNotice(root, message);
  };
}

/**
 * Connection state, always on screen. ADR-0011 requires this rather than
 * leaving it implicit: "Offline editing happens without a lock, which is a
 * different state from holding a lease. The UI must say so ('editing
 * offline, not reserved') or users assume the same protection they get
 * online." Until now nothing said so at all — a dropped connection was
 * invisible until a save failed.
 *
 * Text, not just colour (invariant #11), and `role="status"` so the change is
 * announced politely rather than silently repainted.
 */
function wireConnectionIndicator(root: HTMLElement, transport: Transport): void {
  const el = root.querySelector<HTMLElement>('#wlConn');
  if (!el) return;
  const render = (state: TransportState): void => {
    const label = state === 'open' ? 'Online' : state === 'connecting' ? 'Reconnecting…' : 'Offline';
    el.className = `cf-conn is-${state}`;
    el.title = state === 'open'
      ? 'Connected — edits are reserved while you hold a record.'
      : 'Not connected — nothing is reserved for you, and changes are not saved yet.';
    el.innerHTML = `<span class="cf-conn__dot" aria-hidden="true"></span>${escapeHtml(label)}`;
  };
  // Wired *before* `connect()` so no transition can be missed — but the
  // transport's initial state is 'closed', and painting "Offline" for the
  // microtask before `openSocket()` flips it would flash a claim we cannot
  // support on every single page load. The honest first paint is
  // "Reconnecting…": a connection is about to be attempted.
  render(transport.state === 'closed' ? 'connecting' : transport.state);
  transport.onStateChange(render);
}

export function wireBackButton(root: HTMLElement): void {
  root.querySelector('#backBtn')?.addEventListener('click', () => window.location.assign(appHref('/')));
  // Every page that puts the shell up also needs its nav to work, and every
  // one of them already calls this. A second function nobody remembers to
  // call is a nav that silently stops overflowing on one page.
  wireNavOverflow(root);
}
